import {create} from 'zustand';
import {ATC_ROLE, PILOT_ROLE, getMe, postLogout, type IUser} from '@/api/auth';
import {clearTokens, getAccessToken, getRefreshToken} from '@/lib/token-storage';

type TAuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface IAuthStore {
  status: TAuthStatus;
  user: IUser | null;
  /** Upper-cased FIR codes from `fir_assignments`, in assignment order. */
  firCodes: string[];
  setUser: (user: IUser | null) => void;
  /** Restores the session from the stored tokens (page reload). */
  restore: () => Promise<void>;
  logout: () => Promise<void>;
}

export const readFirCodes = (user: IUser | null): string[] =>
  (user?.fir_assignments ?? [])
    .map((a) => a?.fir_code)
    .filter((code): code is string => typeof code === 'string' && code.length > 0)
    .map((code) => code.trim().toUpperCase());

export const isAtcUser = (user: IUser | null) => user?.role === ATC_ROLE;

export const isPilotUser = (user: IUser | null) => user?.role === PILOT_ROLE;

/** Who the console serves: controllers (ATC home) and pilots (Flight Agent). */
export const canUseConsole = (user: IUser | null) => isAtcUser(user) || isPilotUser(user);

export const userFullName = (user: IUser | null) =>
  user?.full_name?.trim() ||
  `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() ||
  user?.email ||
  '';

export const useAuthStore = create<IAuthStore>((set) => ({
  status: 'loading',
  user: null,
  firCodes: [],
  setUser: (user) =>
    set({
      user,
      firCodes: readFirCodes(user),
      status: user ? 'authenticated' : 'anonymous'
    }),
  restore: async () => {
    if (!getAccessToken() && !getRefreshToken()) {
      set({status: 'anonymous', user: null, firCodes: []});
      return;
    }
    const res = await getMe();
    if (res?.error || !res?.data) {
      clearTokens();
      set({status: 'anonymous', user: null, firCodes: []});
      return;
    }
    set({user: res.data, firCodes: readFirCodes(res.data), status: 'authenticated'});
  },
  logout: async () => {
    await postLogout(getAccessToken(), getRefreshToken()).catch(() => undefined);
    clearTokens();
    set({status: 'anonymous', user: null, firCodes: []});
  }
}));

/** The controller's FIR. The list endpoints take one fir_code, so like the app
 * a controller with several assignments works their first one. */
export const useFirCode = () => useAuthStore((s) => s.firCodes[0]);
